import type { QueryParams } from '@/lib/types/common.types';
import type {
  Certification,
  PortfolioItem,
  SupplierProfile,
  WorkingHours,
} from '@/lib/types/supplier.types';
import apiClient from './client';
import { extractArrayResponse, extractResponseData, handleApiError } from './responseHelpers';

// Re-export types for convenience
export type {
  Certification,
  PortfolioItem,
  SupplierProfile,
  WorkingHours,
} from '@/lib/types/supplier.types';

export interface CreateSupplierProfileData {
  serviceDescription: string;
  categories?: Array<{ categoryId: string; isPrimary: boolean }>;
  workingHours?: WorkingHours;
}

export interface UpdateSupplierProfileData {
  serviceDescription?: string;
  responseTime?: number;
  acceptanceRate?: number;
  onTimeDelivery?: number;
  workingHours?: WorkingHours;
  categories?: Array<{ categoryId: string; isPrimary: boolean }>;
}

export interface PortfolioItemData {
  title: string;
  description: string;
  images: string[];
  category: string;
  completedAt: string;
  projectId?: string;
  clientName?: string;
}

export interface CertificationData {
  name: string;
  issuer: string;
  issuedAt: string;
  expiryDate: string | null;
  fileUrl: string;
  certificateNumber?: string;
}

export const suppliersApi = {
  // Supplier Management
  createProfile: async (profileData: CreateSupplierProfileData): Promise<SupplierProfile> => {
    try {
      // Transform categories from array of objects to array of UUIDs + primary_category_id
      const requestData: any = { ...profileData };

      if (profileData.categories && Array.isArray(profileData.categories)) {
        // Extract category IDs
        requestData.categories = profileData.categories.map((cat) => cat.categoryId);

        // Find and set primary category ID (required for create)
        const primaryCategory = profileData.categories.find((cat) => cat.isPrimary);
        if (primaryCategory) {
          requestData.primaryCategoryId = primaryCategory.categoryId;
        } else if (profileData.categories.length > 0) {
          // If no primary is specified, use the first one
          requestData.primaryCategoryId = profileData.categories[0].categoryId;
        }
      }

      const response = await apiClient.post<
        { message?: string; data: SupplierProfile } | SupplierProfile
      >('/v1/suppliers/profile', requestData);
      return extractResponseData<SupplierProfile>(response);
    } catch (error) {
      handleApiError(error);
    }
  },

  getMyProfile: async (): Promise<SupplierProfile> => {
    try {
      const response = await apiClient.get<{ data: SupplierProfile } | SupplierProfile>(
        '/v1/suppliers/me'
      );
      return extractResponseData<SupplierProfile>(response);
    } catch (error: any) {
      // If profile doesn't exist (404), return null instead of throwing
      // This allows the form to work for new suppliers who haven't created their profile yet
      if (error?.response?.status === 404 || error?.status === 404) {
        throw error; // Re-throw to be handled by the hook
      }
      handleApiError(error);
    }
  },

  updateProfile: async (profileData: UpdateSupplierProfileData): Promise<SupplierProfile> => {
    // Build request data, only including provided fields
    const requestData: any = {};

    // Add serviceDescription if provided
    if (profileData.serviceDescription !== undefined) {
      requestData.serviceDescription = profileData.serviceDescription;
    }

    // Only include categories if the array has at least 1 item (backend requires 1-3)
    if (
      profileData.categories &&
      Array.isArray(profileData.categories) &&
      profileData.categories.length > 0
    ) {
      // Extract category IDs
      requestData.categories = profileData.categories.map((cat) => cat.categoryId);

      // Find and set primary category ID
      const primaryCategory = profileData.categories.find((cat) => cat.isPrimary);
      if (primaryCategory) {
        requestData.primaryCategoryId = primaryCategory.categoryId;
      } else {
        // If no primary is specified, use the first one
        requestData.primaryCategoryId = profileData.categories[0].categoryId;
      }
    }

    // Add workingHours if provided
    if (profileData.workingHours !== undefined) {
      requestData.workingHours = profileData.workingHours;
    }

    // Note: responseTime, acceptanceRate, and onTimeDelivery are calculated fields
    // and cannot be updated directly - they are excluded from the request

    try {
      const response = await apiClient.put<
        { message?: string; data: SupplierProfile } | SupplierProfile
      >('/v1/suppliers/me', requestData);
      return extractResponseData<SupplierProfile>(response);
    } catch (error: any) {
      // If profile doesn't exist (404), create it instead
      if (error.response?.status === 404) {
        // Convert update data to create data format
        // Ensure serviceDescription is provided (required for create)
        if (!profileData.serviceDescription) {
          throw new Error('serviceDescription is required to create a profile');
        }

        const createRequestData: any = {
          serviceDescription: profileData.serviceDescription,
        };

        if (profileData.categories && Array.isArray(profileData.categories)) {
          createRequestData.categories = profileData.categories.map((cat) => cat.categoryId);
          const primaryCategory = profileData.categories.find((cat) => cat.isPrimary);
          if (primaryCategory) {
            createRequestData.primaryCategoryId = primaryCategory.categoryId;
          } else if (profileData.categories.length > 0) {
            createRequestData.primaryCategoryId = profileData.categories[0].categoryId;
          }
        }

        if (profileData.workingHours) {
          createRequestData.workingHours = profileData.workingHours;
        }

        // Create the profile
        const createResponse = await apiClient.post<
          { message?: string; data: SupplierProfile } | SupplierProfile
        >('/v1/suppliers/profile', createRequestData);
        return extractResponseData<SupplierProfile>(createResponse);
      }
      handleApiError(error);
    }
  },

  deleteProfile: async (): Promise<void> => {
    await apiClient.delete('/v1/suppliers/me');
  },

  // Portfolio
  addPortfolioItem: async (itemData: PortfolioItemData): Promise<PortfolioItem> => {
    try {
      const response = await apiClient.post<
        { message?: string; data: PortfolioItem } | PortfolioItem
      >('/v1/suppliers/me/portfolio', itemData);
      return extractResponseData<PortfolioItem>(response);
    } catch (error) {
      handleApiError(error);
    }
  },

  updatePortfolioItem: async (
    id: string,
    itemData: Partial<PortfolioItemData>
  ): Promise<PortfolioItem> => {
    try {
      const response = await apiClient.put<
        { message?: string; data: PortfolioItem } | PortfolioItem
      >(`/v1/suppliers/me/portfolio/${id}`, itemData);
      return extractResponseData<PortfolioItem>(response);
    } catch (error) {
      handleApiError(error);
    }
  },

  deletePortfolioItem: async (id: string): Promise<void> => {
    await apiClient.delete(`/v1/suppliers/me/portfolio/${id}`);
  },

  // Certifications
  addCertification: async (certData: CertificationData): Promise<Certification> => {
    try {
      const response = await apiClient.post<
        { message?: string; data: Certification } | Certification
      >('/v1/suppliers/me/certifications', certData);
      return extractResponseData<Certification>(response);
    } catch (error) {
      handleApiError(error);
    }
  },

  deleteCertification: async (id: string): Promise<void> => {
    await apiClient.delete(`/v1/suppliers/me/certifications/${id}`);
  },

  // Working Hours
  updateWorkingHours: async (workingHours: WorkingHours): Promise<SupplierProfile> => {
    try {
      const response = await apiClient.put<
        { message?: string; data: SupplierProfile } | SupplierProfile
      >('/v1/suppliers/me/working-hours', workingHours);
      return extractResponseData<SupplierProfile>(response);
    } catch (error) {
      handleApiError(error);
    }
  },

  // Verification
  requestVerification: async (): Promise<void> => {
    await apiClient.post('/v1/suppliers/me/verification/request');
  },

  cancelVerification: async (): Promise<void> => {
    await apiClient.delete('/v1/suppliers/me/verification/cancel');
  },

  // Statistics
  getStatistics: async () => {
    try {
      const response = await apiClient.get<{ data: any } | any>('/v1/suppliers/me/statistics');
      return extractResponseData<any>(response);
    } catch (error) {
      handleApiError(error);
    }
  },

  getEarnings: async (
    params?: QueryParams
  ): Promise<{ total: number; byPeriod: Array<{ period: string; amount: number }> }> => {
    try {
      const response = await apiClient.get<
        | { data: { total: number; byPeriod: Array<{ period: string; amount: number }> } }
        | { total: number; byPeriod: Array<{ period: string; amount: number }> }
      >('/v1/suppliers/me/earnings', { params });
      return extractResponseData<{
        total: number;
        byPeriod: Array<{ period: string; amount: number }>;
      }>(response);
    } catch (error) {
      handleApiError(error);
    }
  },

  getPerformance: async (): Promise<{
    responseTime: number;
    acceptanceRate: number;
    onTimeDelivery: number;
  }> => {
    try {
      const response = await apiClient.get<
        | { data: { responseTime: number; acceptanceRate: number; onTimeDelivery: number } }
        | { responseTime: number; acceptanceRate: number; onTimeDelivery: number }
      >('/v1/suppliers/me/performance');
      return extractResponseData<{
        responseTime: number;
        acceptanceRate: number;
        onTimeDelivery: number;
      }>(response);
    } catch (error) {
      handleApiError(error);
    }
  },

  // Public Search & View
  getAll: async (params?: QueryParams): Promise<SupplierProfile[]> => {
    try {
      const response = await apiClient.get<
        { data: SupplierProfile[]; pagination?: any } | SupplierProfile[]
      >('/v1/suppliers', { params });
      return extractArrayResponse<SupplierProfile>(response);
    } catch (error) {
      handleApiError(error);
    }
  },

  getById: async (id: string): Promise<SupplierProfile> => {
    try {
      const response = await apiClient.get<{ data: SupplierProfile } | SupplierProfile>(
        `/v1/suppliers/${id}`
      );
      return extractResponseData<SupplierProfile>(response);
    } catch (error) {
      handleApiError(error);
    }
  },

  getReviews: async (id: string, params?: QueryParams) => {
    try {
      const response = await apiClient.get<{ data: any[]; pagination?: any }>(
        `/v1/suppliers/${id}/reviews`,
        { params }
      );
      return extractArrayResponse<any>(response);
    } catch (error) {
      handleApiError(error);
    }
  },

  getTopRated: async (params?: QueryParams): Promise<SupplierProfile[]> => {
    try {
      const response = await apiClient.get<
        { data: SupplierProfile[]; pagination?: any } | SupplierProfile[]
      >('/v1/suppliers/top-rated', { params });
      return extractArrayResponse<SupplierProfile>(response);
    } catch (error) {
      handleApiError(error);
    }
  },

  getByCategory: async (categoryId: string, params?: QueryParams): Promise<SupplierProfile[]> => {
    try {
      const response = await apiClient.get<
        { data: SupplierProfile[]; pagination?: any } | SupplierProfile[]
      >(`/v1/suppliers/category/${categoryId}`, { params });
      return extractArrayResponse<SupplierProfile>(response);
    } catch (error) {
      handleApiError(error);
    }
  },
};
